import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import type { PageBlock } from '@/payload/payload-types-block';
import { formattedPrice } from '@/ui-kit/price';

import { GiftCardPayButtons } from '../GiftCardPayButtons/GiftCardPayButtons';
import { styles } from './CardParamsPreview.styles';

type GiftCardCreateDesignCard = NonNullable<
  PageBlock<'giftCardCreate'>['designCards']
>[number];

const getCardImageUrl = (card: GiftCardCreateDesignCard) =>
  typeof card.image === 'object' && card.image !== null ? card.image.url || '' : '';

export interface CardParamsPreviewProps {
  selectedCard: GiftCardCreateDesignCard;
  nominal: number;
  quantity: number;
  policyLink: string;
  isPayButtonDisabled?: boolean;
}

export function CardParamsPreview({
  selectedCard,
  nominal,
  quantity,
  policyLink,
  isPayButtonDisabled
}: CardParamsPreviewProps) {
  const slots = styles();

  const t = useTranslations();

  const summaryList = [
    {
      label: t('nominal'),
      value: nominal === 0 ? t('giftCard.notSelected') : formattedPrice(nominal)
    },
    {
      label: t('count'),
      value: `${quantity} ${t('pcs')}`
    }
  ];
  return (
    <div className={slots.base()}>
      <div className={slots.imageWrapper()}>
        <Image
          src={getCardImageUrl(selectedCard)}
          fill
          alt=""
          className={slots.image()}
        />
      </div>

      <div>
        <div className={slots.summaryList()}>
          {summaryList.map((item) => (
            <div
              key={item.label}
              className={slots.summaryRow()}
            >
              <span className={slots.summaryLabel()}>{item.label}</span>
              <span
                className={slots.summaryDots()}
                aria-hidden
              />
              <span className={slots.summaryValue()}>{item.value}</span>
            </div>
          ))}
        </div>
        <div className={slots.totalRow()}>
          <span className={slots.totalLabel()}>{t('total')}</span>
          <span className={slots.totalValue()}>{formattedPrice(nominal * quantity)}</span>
        </div>
      </div>

      <GiftCardPayButtons
        className={slots.payButtons()}
        disabled={isPayButtonDisabled}
        loading={isPayButtonDisabled}
      />
      <div className={slots.policy()}>
        {t.rich('giftCard.privacy', {
          link: (chunks) => (
            <Link
              href={policyLink}
              className={slots.policyLink()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </Link>
          )
        })}
      </div>
    </div>
  );
}
